import React, { useCallback, useContext, useEffect, useState } from 'react';

import styled from 'styled-components';

import { APIProvider, APIResponse } from '@cd3p/core/types/api';
import { phoneField, textField } from '@cd3p/core/utils/fields';
import {
  ConfirmationDialog,
  FormField,
  Modal,
  TypeaheadFormField,
} from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';
import { LocationChangeConfirmationContext } from 'providers/LocationChangeConfirmationProvider';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useAdminCustomerAccountsList } from 'modules/adminCustomerAccountsList';
import { useCache } from 'modules/cache';

import { adminCustomerAccountsList } from 'selectors';

import { TypeaheadOption } from 'types/app';

const FormWrapper = styled.form`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
  flex-grow: 1;
`;

enum AddFormFields {
  Name = 'name',
  Address = 'address',
  PhoneNumber = 'phoneNumber',
}

type AddForm = {
  [AddFormFields.Name]: string;
  [AddFormFields.Address]: TypeaheadOption | null;
  [AddFormFields.PhoneNumber]: string;
};

const defaultValues: AddForm = {
  [AddFormFields.Name]: '',
  [AddFormFields.Address]: null,
  [AddFormFields.PhoneNumber]: '',
};

type Props = {
  onClosePopup: () => void;
  onSuccess?: (result: APIProvider) => void;
};

export const AddCustomerAccountPopup: React.FC<Props> = ({
  onClosePopup,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const { loadAddress } = useCache();

  const { createCustomerAccount } = useAdminCustomerAccountsList();

  const isFormPending = useSelector(
    adminCustomerAccountsList.createCustomerAccountPending,
  );

  const {
    setValue,
    handleSubmit,
    control,
    formState: { isValid, errors, isDirty },
  } = useForm<AddForm>({
    defaultValues,
  });

  const {
    continueChangeLocation,
    addLocationChangeConfirmation,
    removeLocationChangeConfirmation,
  } = useContext(LocationChangeConfirmationContext);

  const [confirmNavigate, setConfirmNavigate] = useState(false);

  const onLocationChange = useCallback(() => {
    if (isDirty) {
      setConfirmNavigate(true);
      return false;
    }
  }, [isDirty]);

  useEffect(() => {
    return addLocationChangeConfirmation(onLocationChange);
  }, [addLocationChangeConfirmation, onLocationChange]);

  const loadAddressOptions = async (value: string) => {
    const result: APIResponse<string[]> = await loadAddress(value);
    let options: TypeaheadOption[] = [];
    if (!result.error) {
      options = result.payload.map(it => ({
        label: it,
        value: it,
      }));
    }
    return {
      options,
    };
  };

  const handleApiResult = useHandleApiResult();

  const onSubmit = async (data: AddForm) => {
    if (!isValid) {
      return;
    }
    handleApiResult<APIProvider>(
      () =>
        createCustomerAccount({
          providerName: data[AddFormFields.Name].trim(),
          officeAddress: data[AddFormFields.Address]?.value || null,
          phone: data[AddFormFields.PhoneNumber],
        }),
      ({ result, showBaseToast }) => {
        showBaseToast(
          t('customers.support.customerAccountCreatedSuccess', {
            providerName: result.payload.providerName,
          }),
        );
        removeLocationChangeConfirmation(onLocationChange);
        onClosePopup?.();
        onSuccess?.(result.payload);
      },
    );
  };

  const onSelectAddress = useCallback(
    (option: TypeaheadOption | null) => {
      setValue(AddFormFields.Address, option, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const handleConfirmNavigate = useCallback(() => {
    setConfirmNavigate(false);
    continueChangeLocation();
  }, [continueChangeLocation]);

  return (
    <>
      <ConfirmationDialog
        onClose={() => setConfirmNavigate(false)}
        open={confirmNavigate}
        handleActionClick={handleConfirmNavigate}
        description={t('common.exitConfirmationWindow.text')}
        cancelButtonTitle={t('common.continueEditing')}
        actionButtonTitle={t('common.confirmCancel')}
      />
      <Modal
        open
        header={t('customers.support.newAccountTitle')}
        confirmLabel={t('customers.support.newAccountSubmit')}
        onConfirm={handleSubmit(onSubmit)}
        isDisabled={isFormPending}
        isPending={isFormPending}
        onClose={onClosePopup}
        width="50rem"
        contentWrapperStyle={{ overflow: 'visible' }}
      >
        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
          <FormField
            fieldName={AddFormFields.Name}
            fieldError={errors[AddFormFields.Name] as FieldError}
            label={t('customers.addCustomerPopup.companyNameLabel')}
            placeholder={t('customers.addCustomerPopup.companyNamePlaceholder')}
            requiredErrorMessage={t('common.form.error.emptyField')}
            control={control}
            maxLength={textField.maxLength}
            isRequired
            isDisabled={isFormPending}
            inputProps={{
              maxLength: textField.maxLength,
            }}
          />
          <FormField
            fieldName={AddFormFields.Address}
            label={t('customers.addCustomerPopup.companyAddressLabel')}
            fieldError={errors[AddFormFields.Address] as FieldError}
            requiredErrorMessage={t('common.form.error.emptyField')}
            control={control}
            render={() => (
              <TypeaheadFormField
                placeholder={t(
                  'customers.addCustomerPopup.companyAddressPlaceholder',
                )}
                hasError={!!errors[AddFormFields.Address]}
                loadOptions={loadAddressOptions}
                onChange={onSelectAddress}
                isDisabled={isFormPending}
              />
            )}
          />
          <FormField
            showError
            isRequired={false}
            fieldName={AddFormFields.PhoneNumber}
            label={t('settings.companySettings.form.companyPhoneLabel')}
            placeholder={t(
              'settings.companySettings.form.companyPhonePlaceholder',
            )}
            control={control}
            inputProps={{
              maxLength: phoneField.maxLength,
            }}
            fieldError={errors[AddFormFields.PhoneNumber] as FieldError}
            mask={phoneField.mask}
            maskDefinitions={phoneField.maskDefinitions}
            rules={{
              validate: {
                incorrectPhone: (value: string) =>
                  !value ||
                  phoneField.validate(value) ||
                  t('common.form.phone.error.invalid'),
              },
            }}
          />
        </FormWrapper>
      </Modal>
    </>
  );
};
